import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, KeyRound, ShieldCheck, AlertCircle, CheckCircle2, Mail } from "lucide-react";
import { api } from "@/lib/api";

const RULES = [
  { key: "len",     label: "At least 10 characters", ok: (p) => (p || "").length >= 10 },
  { key: "letter",  label: "At least one letter",    ok: (p) => /[A-Za-z]/.test(p || "") },
  { key: "digit",   label: "At least one number",    ok: (p) => /\d/.test(p || "") },
  { key: "special", label: "At least one special character", ok: (p) => /[^A-Za-z0-9]/.test(p || "") },
];

export function StrengthMeter({ password }) {
  return (
    <ul className="mt-2 space-y-1" data-testid="pw-strength">
      {RULES.map(r => {
        const ok = r.ok(password);
        return (
          <li key={r.key} className={`text-xs flex items-center gap-1.5 ${ok ? "text-teal-600" : "text-slate-500"}`} data-testid={`pw-rule-${r.key}`}>
            {ok ? <CheckCircle2 className="w-3 h-3" /> : <span className="w-3 h-3 rounded-full border border-slate-300" />}
            {r.label}
          </li>
        );
      })}
    </ul>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      // Always succeed for privacy — server also returns 200 for unknown emails.
      setSent(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16" data-testid="forgot-password-page">
      <Card className="max-w-md w-full border-slate-200">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-2xl bg-teal-600 mx-auto flex items-center justify-center teal-glow">
              <KeyRound className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-display font-bold text-2xl mt-4 text-slate-900">Forgot your password?</h2>
            <p className="text-sm text-slate-500 mt-2">Enter your email and we'll send you a secure reset link.</p>
          </div>
          {!sent ? (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="forgot-email" className="mt-1.5" />
              </div>
              <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-3" data-testid="forgot-sent">
              <Mail className="w-10 h-10 text-teal-600 mx-auto" />
              <div className="text-slate-800 font-semibold">Check your inbox</div>
              <p className="text-sm text-slate-600">If an account exists for that email, we've sent a reset link that's valid for 60 minutes.</p>
            </div>
          )}
          <div className="text-center text-sm text-slate-600 mt-5">
            <Link to="/login" className="text-teal-600 font-semibold" data-testid="forgot-to-login">Back to sign in</Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [state, setState] = useState({ loading: true, valid: false, reason: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setState({ loading: false, valid: false, reason: "not_found_or_used" }); return; }
    api.get(`/auth/reset-password/verify?token=${encodeURIComponent(token)}`)
      .then(r => setState({ loading: false, valid: !!r.data.valid, reason: r.data.reason || "" }))
      .catch(() => setState({ loading: false, valid: false, reason: "not_found_or_used" }));
  }, [token]);

  const allValid = RULES.every(r => r.ok(password)) && password === confirm && confirm.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!allValid) return toast.error("Password does not meet all requirements");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated — please sign in");
      setTimeout(() => nav("/login", { replace: true }), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-16" data-testid="reset-password-page">
      <Card className="max-w-md w-full border-slate-200">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-2xl bg-teal-600 mx-auto flex items-center justify-center teal-glow">
              <ShieldCheck className="w-6 h-6 text-white" />
            </div>
            <h2 className="font-display font-bold text-2xl mt-4 text-slate-900">Choose a new password</h2>
          </div>
          {state.loading ? (
            <div className="text-center text-sm text-slate-500 py-8" data-testid="reset-loading">Verifying link…</div>
          ) : !state.valid ? (
            <div className="space-y-3" data-testid="reset-invalid">
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <div>
                  {state.reason === "expired"
                    ? "This reset link has expired. Request a new one to continue."
                    : "This reset link is invalid or has already been used."}
                </div>
              </div>
              <Button onClick={() => nav("/forgot-password")} className="w-full bg-slate-900 text-white" data-testid="reset-request-new">
                Request a new link
              </Button>
            </div>
          ) : done ? (
            <div className="text-center space-y-3" data-testid="reset-done">
              <CheckCircle2 className="w-10 h-10 text-teal-600 mx-auto" />
              <div className="text-slate-800 font-semibold">Password updated</div>
              <p className="text-sm text-slate-600">Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>New password</Label>
                <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} data-testid="reset-pw" className="mt-1.5" />
                <StrengthMeter password={password} />
              </div>
              <div>
                <Label>Confirm new password</Label>
                <Input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)} data-testid="reset-confirm" className="mt-1.5" />
                {confirm && confirm !== password && (
                  <div className="text-xs text-red-600 mt-1">Passwords do not match</div>
                )}
              </div>
              <Button type="submit" disabled={!allValid || submitting} data-testid="reset-submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11 disabled:bg-slate-300 disabled:cursor-not-allowed">
                {submitting ? "Updating…" : "Update password"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
